import { useNavigate } from 'react-router-dom';
import { Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { LeftOutlined } from '@ant-design/icons';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { ContentCustom, PageHeaderCustom } from '../../../../components';
import { useErrorMessage } from '../../../../utils/errorMessage';
import { ListReviews } from '../reviews/ListReviews';
import { createFileFromJSON } from '../../../../utils/filesManagement/createFileFromJSON';
import { useDownloadDocument } from '../../../../utils/downloadDoc';
import { useViewDocument } from '../../../../utils/filesManagement/viewDocument';
import { ListDocuments } from '../../../../components/ListDocuments/ListDocuments';
import { LoadingModal } from '../../../../components/LoadingModal/LoadingModal';
import { GeneralInfos } from './detailList/GeneralInfos';
import { Otherinfos } from './detailList/OtherInfos';
import { ExtraInfos } from './detailList/ExtraInfos';
import { CardTitle } from '../../../../components/CardTitle/CardTitle';
import { useSocketContext } from '../../../../contexts/SocketContext';
import { profileLockMessage } from '../../utils/profileLockMessage';

/**
 * React component for displaying a user profile.
 * @component
 * @returns {JSX.Element} - React component.
 */
export const ShowProfile = () => {
  const [profile, setProfile] = useState({});
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { t } = useTranslation();
  const { userProfile, user, dispatchAPI, token } = useAuthContext();
  const { message } = useErrorMessage();
  const { downloadDocument } = useDownloadDocument();
  const { viewDocument } = useViewDocument(setPreviewLoading);
  const navigate = useNavigate();
  const { onProfileLock, profileLock } = useSocketContext();

  const personalDocumentsKeys = new Set([
    'criminal_record_extract',
    'identification_photocopy'
  ]);
  const secondaryPersonalDocumentsKeys = new Set([
    'secondary_profile_identification_photocopy',
    'secondary_profile_criminal_record_extract'
  ]);
  const role = `${user?.role?.split(':')[1].toLowerCase()}s`;
  const personalDocuments = profile?.documents?.filter((doc) =>
    personalDocumentsKeys.has(doc?.type)
  );
  const secondaryPersonalDocuments =
    profile?.secondary_profile?.documents?.filter((doc) =>
      secondaryPersonalDocumentsKeys.has(doc?.type)
    );
  const otherInfosDocumentsKeys = new Set([
    'early_retirement_certificate',
    'insurance_certificate',
    'residence_proof',
    'other_documents'
  ]);
  const otherInfosDocuments = profile?.documents?.filter((doc) =>
    otherInfosDocumentsKeys.has(doc?.type)
  );
  const pensionerRole = user?.role === 'users:PENSIONER';
  const checkProfileLock = profileLock.some(
    (item) => item.type === profile._id
  );

  const documentsTypesArray = new Set([
    'identification_photo',
    'secondary_profile_identification_photo'
  ]);
  const photoId = (profileData) => {
    const documentType = profileData?.documents?.find((doc) =>
      documentsTypesArray.has(doc.type)
    );

    return documentType?._id?._id;
  };

  const getProfile = async () => {
    try {
      setIsLoading(true);
      const { data } = await dispatchAPI('GET', {
        url: `/${role}/profile-by-user?email=${user?.email}&populate=photo${
          pensionerRole
            ? ',documents._id,secondary_profile.documents._id,secondary_profile.photo'
            : ''
        }`
      });

      setProfile(data);

      if (data.secondary_profile?.photo) {
        const secondaryProfilePhoto = await createFileFromJSON(
          data.secondary_profile.photo,
          dispatchAPI,
          message
        );
        setProfile((prevState) => ({
          ...prevState,
          secondary_profile: {
            ...data.secondary_profile,
            photo: secondaryProfilePhoto
          }
        }));
      }
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      await getProfile();
      setIsLoading(false);
    })();
  }, [userProfile]);

  const getRatings = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/ratings?author.id=${user._id}&populate=author.id,receiver.id`
      });
      setReviews(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      await getRatings();
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await onProfileLock();
    })();
  }, []);

  return (
    <>
      <PageHeaderCustom
        withoutBreadCrumb
        backIcon={<LeftOutlined />}
        title={t('dashboard.cards_titles.my_profile')}
      />
      <ContentCustom transparent style={{ gap: 24 }}>
        <GeneralInfos
          token={token}
          profile={profile}
          t={t}
          photoId={pensionerRole ? photoId(profile) : profile?.photo?._id}
          user={user}
          isLoading={isLoading}
          navigate={navigate}
          profileID={profile?._id}
          onPreview={viewDocument}
          personalDocuments={personalDocuments}
          downloadDocument={downloadDocument}
          checkProfileLock={checkProfileLock}
          profileLockMessage={profileLockMessage}
        />
        {['first_name', 'last_name'].every((item) =>
          Object.keys(profile?.secondary_profile ?? []).includes(item)
        ) &&
          pensionerRole && (
            <GeneralInfos
              token={token}
              photoId={photoId(profile?.secondary_profile)}
              profile={profile?.secondary_profile || {}}
              t={t}
              user={user}
              navigate={navigate}
              profileID={profile?._id}
              onPreview={viewDocument}
              isLoading={isLoading}
              personalDocuments={secondaryPersonalDocuments}
              downloadDocument={downloadDocument}
              checkProfileLock={checkProfileLock}
              profileLockMessage={profileLockMessage}
            />
          )}
        {pensionerRole && (
          <>
            <Otherinfos
              isLoading={isLoading}
              profile={profile}
              t={t}
              navigate={navigate}
              checkProfileLock={checkProfileLock}
              profileLockMessage={profileLockMessage}
            />
            <Card
              title={
                <CardTitle icon="file">
                  {t('pensioners.show.document_info')}
                </CardTitle>
              }
            >
              <ListDocuments
                onPreview={viewDocument}
                isLoading={isLoading}
                documents={otherInfosDocuments}
                t={t}
                downloadDocument={downloadDocument}
              />
            </Card>
            <ExtraInfos
              profile={profile}
              t={t}
              navigate={navigate}
              isLoading={isLoading}
              checkProfileLock={checkProfileLock}
              profileLockMessage={profileLockMessage}
            />
          </>
        )}
        {Object.keys(reviews).length > 0 && (
          <Card>
            <ListReviews user={user} t={t} reviews={reviews} />
          </Card>
        )}
      </ContentCustom>
      <LoadingModal
        openModal={previewLoading}
        loadingText="files.modal.preparing_document"
      />
    </>
  );
};
